import qs from 'query-string';
import { AUTHENTICATION_PATH, BASE_URL, BET_PATH, BETS_PATH, BONUS_PATH } from './betting-service-constants';
import reportSportsbookError from '~/components/sportsbook/lobby/utils/sportsbook-error-reporter';
import {
	AcceptModeResponse,
	AuthenticationRefreshResponse,
	Bet,
	BetBuilderResponseItem,
	BetsResponse,
	CalculateCashoutResponse,
	CheckBetsRequest,
	CheckBetsResponse,
	ListActiveBonusesResponse,
	PlaceBetsRequest,
	PlaceBetsResponse,
	SubmitCashoutRequest,
	SubmitCashoutResponse,
	UpdateAcceptModeRequest
} from '~/components/sportsbook/lobby/api/betting-service/betting-service-types';
import { BetStatus, OddsFormat } from '~/components/sportsbook/lobby/sportsbook-types';

const OPEN = 'open';
const SETTLED = 'settled';
const ABORT_ERROR_NAME = 'AbortError';

// YANAPIC ( Yet Another API Client... )

type BettingServiceError = {
	error: string;
};

type BettingServiceResponse<T> = BettingServiceError | T;

const isBettingServiceError = <T>(response: BettingServiceResponse<T>): response is BettingServiceError =>
	(response as BettingServiceError).error !== undefined;

export type SerializableParameter = string | string[] | boolean | undefined;

export default class BettingServiceApiClient {
	private franchiseCode: string;

	constructor(franchiseCode: string) {
		this.franchiseCode = franchiseCode;
	}

	public getFranchiseCode = () => this.franchiseCode;

	public refresh = async (franchiseCode: string, uuid: string): Promise<AuthenticationRefreshResponse> =>
		this.postRequest(`${AUTHENTICATION_PATH}/refresh`, { franchiseCode, uuid });

	public checkBets = async (req: CheckBetsRequest, abortSignal: AbortSignal): Promise<CheckBetsResponse> =>
		this.postRequest(`${BET_PATH}/checkBets`, req, undefined, abortSignal);

	public placeBets = async (req: PlaceBetsRequest): Promise<PlaceBetsResponse> =>
		this.postRequest(`${BET_PATH}/placeBets`, req);

	public updateAcceptMode = async (req: UpdateAcceptModeRequest): Promise<AcceptModeResponse> =>
		this.postRequest(`${BET_PATH}/acceptMode`, req);

	public submitCashout = async (req: SubmitCashoutRequest): Promise<SubmitCashoutResponse> =>
		this.postRequest('/public/cashout/submit', req);

	public generateBetBuilderUrl = async (betBuilderId: string, oddsFormat: OddsFormat, hasReversedOrder: boolean) => {
		const response = await this.request<{ url: string }>(`/public/betBuilder/generateUrl`, {
			betBuilderId,
			oddsFormat,
			hasReversedOrder
		});
		return response.url;
	};

	public getBetBuilderItem = async (betId: string): Promise<BetBuilderResponseItem> =>
		this.request(`/public/betBuilder/getItem`, { betId });

	public fetchBets = async (
		betStatus: BetStatus,
		startIndex: number,
		pageSize: number,
		startDate?: string,
		endDate?: string
	) => {
		const method = betStatus === BetStatus.UNSETTLED ? OPEN : SETTLED;

		const response = await this.request<BetsResponse>(`${BETS_PATH}/${method}/${startIndex}/${pageSize}`, {
			startDate,
			endDate
		});
		return response;
	};

	public fetchBet = async (betId: number) => this.request<Bet>(`${BETS_PATH}/${betId}`);

	public fetchOpenBetsCount = async () =>
		this.request<number>(`${BETS_PATH}/open/count`)
			.catch(() => 0)
			.then(v => ({ unsettledBetsCount: v }));

	public fetchAcceptMode = async (): Promise<AcceptModeResponse> => this.request(`${BET_PATH}/acceptMode`);

	public listActiveBonuses = async () => this.request<ListActiveBonusesResponse>(`${BONUS_PATH}/activeBonusTokens`);

	public calculateCashOutAllOpenBets = async (): Promise<CalculateCashoutResponse[]> =>
		this.request('/public/cashout/calculateAll');

	private withParams = (path: string, parameters?: { [ref: string]: SerializableParameter }): string => {
		const queryString = parameters && qs.stringify(parameters, { encode: false });
		return `${BASE_URL}${path}?franchiseCode=${this.franchiseCode}${queryString ? `&${queryString}` : ''}`;
	};

	private request = async <T>(path: string, parameters?: { [ref: string]: SerializableParameter }): Promise<T> => {
		const response = <BettingServiceResponse<T>>(
			await fetch(this.withParams(path, parameters)).then(_response => _response.json())
		);
		if (isBettingServiceError(response)) {
			throw new Error(response.error);
		}

		return response;
	};

	private postRequest = async <V, T>(
		path: string,
		data: V,
		parameters?: { [ref: string]: SerializableParameter },
		abortSignal?: AbortSignal
	): Promise<T> => {
		const bodyAsString = JSON.stringify(data);
		const hdrs = {
			method: 'POST',
			mode: 'same-origin',
			credentials: 'same-origin',
			headers: {
				'Content-Type': 'application/json'
			},
			redirect: 'follow',
			referrerPolicy: 'same-origin',
			body: bodyAsString,
			signal: abortSignal
		};
		const route = this.withParams(path, parameters);
		const response = <BettingServiceResponse<T>>await fetch(route, hdrs)
			.then(_response => _response.json())
			.catch(error => {
				// Do not report aborted requests
				if (!(error instanceof Error) || error.name !== ABORT_ERROR_NAME) {
					reportSportsbookError(`${path} error`, error, { urlPath: path, body: bodyAsString });
				}
				throw error;
			});
		if (isBettingServiceError(response)) {
			throw new Error(response.error);
		}
		return response;
	};
}

// BettingServiceApiClient singleton
let bettingServiceApiClient: BettingServiceApiClient | null = null;

export const getBettingServiceApiClient = () => {
	if (bettingServiceApiClient == null) {
		throw new Error('Betting Service client has not been configured');
	}
	return bettingServiceApiClient;
};

export const createBettingServiceApiClient = (franchiseCode: string) => {
	if (!bettingServiceApiClient || bettingServiceApiClient.getFranchiseCode() !== franchiseCode) {
		bettingServiceApiClient = new BettingServiceApiClient(franchiseCode);
	}
};

// HMR for Singleton
// add the old client when disposing the module
// and then create a new one with the same settings
if (import.meta && import.meta.webpackHot) {
	import.meta.webpackHot.dispose(data => {
		// eslint-disable-next-line
		data.bettingServiceApiClient = bettingServiceApiClient;
	});

	// eslint-disable-next-line
	if (import.meta.webpackHot.data?.bettingServiceApiClient) {
		const oldClient = <
			BettingServiceApiClient // eslint-disable-next-line @typescript-eslint/no-unsafe-member-access
		>import.meta.webpackHot.data.bettingServiceApiClient;
		createBettingServiceApiClient(oldClient.getFranchiseCode());
	}
}
